import { call, put, take, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import services from '../../services';
import * as models from '../../models'; 
import * as actions from './actions';
import * as constants from './constants';
import { userActions } from 'store/user';
import { AppToastTypes } from 'models/enums'; 
import { USER_ACTIONS } from 'store/enums';

export default function* () {
    yield takeEvery(constants.REQUEST_CREATE_COMPANY, createCompany);
    yield takeEvery(constants.REQUEST_AGENT_COMPANY_DD, requestAgentCompanyDD); 
    yield takeEvery(constants.REQUEST_MORTGAGE_COMPANY_DD, requestMortgageCompanyDD); 
    yield takeEvery(constants.REQUEST_COMPANY, requestCompany); 
    yield takeEvery(constants.REQUEST_ALL_COMPANIES_DD, requestAllCompaniesDD); 
    yield takeEvery(constants.REQUEST_COMPANY_DROPDOWN, requestCompanyDropdown); 
    yield takeEvery(constants.REQUEST_COMPANY_LIST, requestCompanyList); 
    yield takeEvery(constants.REQUEST_INSURANCE_COMPANY_LIST, requestInsCompanyList); 
    yield takeEvery(constants.REQUEST_INS_COMPANY_DD, requestInsCompanyDD); 
    yield takeEvery(constants.REQUEST_COMPANY_UPDATE, updateCompany);     
    yield takeEvery(constants.REQUEST_COMPANY_FILE_TYPES_LIST, requestCompanyFileTypes); 
    yield takeEvery(constants.REQUEST_ADD_COMPANY_FILE_TYPE, addCompanyFileType);
    yield takeEvery(constants.REQUEST_COMPANY_UPDATE_FILE_TYPE, updateCompanyFileType);
    yield takeEvery(constants.REQUEST_COMPANY_FILES, requestCompanyFiles);
    yield takeEvery(constants.REQUEST_UPLOAD_COMPANY_FILE, uploadCompanyFile);
    yield takeEvery(constants.REQUEST_DELETE_COMPANY_FILE, deleteCompanyFile);
    yield takeEvery(constants.REQUEST_COMPANY_FILE_TYPES_DD, requestCompanyFileTypesDD);
    yield takeEvery(constants.REQUEST_FILE_TYPE_SECTIONS, requestFileTypeSections);
}

export function* createCompany(action: ActionType<typeof actions.createCompany>): any {
    yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_CREATING));
    const userRequest = yield call(services.api.company.createCopmany, action.payload.company);
    if (userRequest.isSuccess) {
        yield put(actions.createCompanySuccess()); 
        yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_CREATE_SUCCESS));
        yield put(userActions.setAppToast({message: "Company Created", type: AppToastTypes.Success})); 
    }
    else {
        console.log(userRequest); 
        yield put(actions.createCompanyFailed()); 
        yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_CREATE_FAILURE));
        const errorMessage = userRequest.getError() || "Failed to create company. Please try again.";
        yield put(userActions.setAppToast({message: errorMessage, type: AppToastTypes.Failure})); 
    }   
}

export function* requestCompany(action: ActionType<typeof actions.requestCompany>): any {
    const userRequest = yield call(services.api.company.getCompanyByID, action.payload.companyId);
    if (userRequest.isSuccess) {
        yield put(actions.receiveCompany(userRequest.data)); 
    }
}

export function* requestCompanyDropdown(action: ActionType<typeof actions.requestCompanyDropdown>): any {
    const userRequest = yield call(services.api.company.getCompanies, action.payload.request);
    if (userRequest.isSuccess) {        
        yield put(actions.receiveCompanyDropdown(userRequest.data)); 
    }    
    else console.log(userRequest); 
}

export function* requestCompanyCSV(action: ActionType<typeof actions.requestCompanyCSV>): any {
    const userRequest = yield call(services.api.company.getCompaniesCSV, action.payload.request); 
    if (userRequest.isSuccess){        
        yield put(actions.receiveCompanyCSV()); 
    }
}

export function* requestCompanyList(action: ActionType<typeof actions.requestCompanyList>): any {
    const userRequest = yield call(services.api.company.getCompanies, action.payload.request); 
    if (userRequest.isSuccess) {
        yield put(actions.receiveCompanyList(userRequest.data)); 
    }
}

export function* requestInsCompanyList(action: ActionType<typeof actions.requestInsCompanyList>): any {
    const companyReqeust: models.CompaniesRequest = {
        ...action.payload.paging,
        type: 'Insurance', 
        activeOnly: false,
        activeStatus: 'all'         
    }
    const userRequest = yield call(services.api.company.getCompanies, companyReqeust); 
    if (userRequest.isSuccess) {        
        yield put(actions.receiveInsCompanyList(userRequest.data)); 
    }    
}

export function* updateCompany(action: ActionType<typeof actions.updateCompany>): any {  
    yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_UPDATING));
    const userRequest = yield call(services.api.company.updateCompany, action.payload.companyId, action.payload.company);    
    if (userRequest.isSuccess) {
        yield put(actions.requestCompany(action.payload.companyId)); 
        yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_UPDATE_SUCCESS));
        yield put(userActions.setAppToast({message: "Company Updated", type: AppToastTypes.Success})); 
    }    
    else {
        console.log(userRequest); 
        yield put(actions.updateCompanyFailed());         
        yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_UPDATE_FAILURE));
        const errorMessage = userRequest.getError() || "Failed to update company. Please try again.";
        yield put(userActions.setAppToast({message: errorMessage, type: AppToastTypes.Failure})); 
    }         
}

// company files
export function* requestCompanyFiles(action: ActionType<typeof actions.requestCompanyFiles>): any {
    const userRequest = yield call(services.api.company.getCompanyFiles, action.payload.request);
    if (userRequest.isSuccess) {
        yield put(actions.receiveCompanyFiles(userRequest.data)); 
    }
        else console.log(userRequest);
}

export function* uploadCompanyFile(action: ActionType<typeof actions.uploadCompanyFile>): any { 
    // 1. Notify system that an upload cycle has started
    yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_UPDATING));
    
    // 2. Execute network boundary proxy dispatch
    const userRequest = yield call(services.api.company.uploadCompanyFile, action.payload.payload);
    
    if (userRequest.isSuccess) {
        yield put(actions.uploadCompanyFileSuccess());
        
        // Refresh target company aggregate state data cache
        yield put(actions.requestCompany(action.payload.payload.companyId));
        
        // 3. Dispatch global confirmation alert payloads
        yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_UPDATE_SUCCESS));
        yield put(userActions.setAppToast({
            message: "Company Reference File Uploaded Successfully", 
            type: AppToastTypes.Success
        }));
    } else {
        console.log(userRequest);
        yield put(actions.uploadCompanyFileFailed());
        
        // 4. Extract explicit errors thrown from the API layer (e.g., storage string errors)
        yield put(userActions.setLastUserAction(USER_ACTIONS.COMPANY_UPDATE_FAILURE));
        const errorMessage = userRequest.getError() || "Failed to upload reference file. Please try again.";
        
        yield put(userActions.setAppToast({
            message: errorMessage, 
            type: AppToastTypes.Failure
        }));
    }
}

export function* deleteCompanyFile(action: ActionType<typeof actions.deleteCompanyFile>): any {
    const userRequest = yield call(services.api.company.deleteCompanyFile, action.payload.fileId, action.payload.companyId);
    if (userRequest.isSuccess) {
        yield put(actions.deleteCompanyFileSuccess());
        yield put(actions.requestCompany(action.payload.companyId));
    } else {
        console.log(userRequest);
        yield put(actions.deleteCompanyFileFailed());
    }
}

// company file types
export function* requestCompanyFileTypes(action: ActionType<typeof actions.requestCompanyFileTypesList>): any {
    const userRequest = yield call(services.api.company.getCompanyFileTypes);   
    if (userRequest.isSuccess) {
        yield put(actions.receiveCompanyFileTypesList(userRequest.data)); 
    }   
    else console.log(userRequest);
}

export function* addCompanyFileType(action: ActionType<typeof actions.addCompanyFileType>): any {
    const userRequest = yield call(services.api.company.addCompanyFileType, action.payload.fileType);
    if (userRequest.isSuccess) {
        yield put(actions.requestCompanyFileTypesList({ pageNo: 1, pageSize: 100 }));
        yield put(actions.addCompanyFileTypeSuccess());
    } else {
        console.log(userRequest);
        yield put(actions.addCompanyFileTypeFailed());
    }
}

export function* updateCompanyFileType(action: ActionType<typeof actions.updateCompanyFileType>): any {
    const userRequest = yield call(services.api.company.updateCompanyFileType, action.payload.fileType);
    if (userRequest.isSuccess) {
        yield put(actions.requestCompanyFileTypesList({ pageNo: 1, pageSize: 100 }));
        yield put(actions.updateCompanyFileTypeSuccess());
    } else {
        console.log(userRequest);
        yield put(actions.updateCompanyFileTypeFailed());
    }
}

// dropdowns
export function* requestAllCompaniesDD(action: ActionType<typeof actions.requestAgentCompanyDD>): any {
    const userRequest = yield call(services.api.company.getAllCompaniesDD);
    if (userRequest.isSuccess) {
       yield put(actions.receiveAllCompanyiesDD(userRequest.data)); 
   }    
   else console.log(userRequest);    
}


export function* requestAgentCompanyDD(action: ActionType<typeof actions.requestAgentCompanyDD>): any {
    const userRequest = yield call(services.api.company.getAgentCompanyDD);
    if (userRequest.isSuccess) {
       yield put(actions.receiveAgentCompanyDD(userRequest.data)); 
   }    
   else console.log(userRequest);    
}

export function* requestInsCompanyDD(action: ActionType<typeof actions.requestInsCompanyDD>): any {
    const userRequest = yield call(services.api.company.getInsCompanyDD);
    if (userRequest.isSuccess) {
       yield put(actions.receiveInsCompanyDD(userRequest.data)); 
   }    
   else console.log(userRequest);    
}

export function* requestMortgageCompanyDD(action: ActionType<typeof actions.requestMortgageCompanyDD>): any {
    const userRequest = yield call(services.api.company.getMortgageCompanyDD);
    if (userRequest.isSuccess) {
       yield put(actions.receiveMortgageCompanyDD(userRequest.data)); 
   }    
   else console.log(userRequest);                     
}

export function* requestCompanyFileTypesDD(action: ActionType<typeof actions.requestCompanyFileTypesDD>): any {
    const userRequest = yield call(services.api.company.getCompanyFileTypesDD);
    if (userRequest.isSuccess) {
       yield put(actions.receiveCompanyFileTypesDD(userRequest.data)); 
   }    
   else console.log(userRequest);    
}   

export function* requestFileTypeSections(action: ActionType<typeof actions.requestFileTypeSections>): any {
    const userRequest = yield call(
        services.api.company.getFileTypeSections,
        action.payload.fileTypeId,
        action.payload.companyId
    );
    if (userRequest.isSuccess) {
         yield put(actions.receiveFileTypeSections(action.payload.fileTypeId, userRequest.data));
    }
    else console.log(userRequest);
}